import React, { useState, useEffect, useCallback } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronLeft, ChevronRight, CalendarCheck, PhoneCall } from 'lucide-react'
import { HeroContent } from '../../../types/content.types'
import { getImageUrl } from '../../../utils/imageUtils'
import { trackCtaClick } from '../../../utils/analytics'
import { useLanguage } from '../../../context/LanguageContext'
import { PHONE_LINK, NAVER_BOOKING_URL } from '../../../constants/contact'

interface HeroProps {
    content: HeroContent
}

const Hero: React.FC<HeroProps> = ({ content }) => {
    const { t } = useLanguage()
    const [currentSlide, setCurrentSlide] = useState(0)
    const [imagesLoaded, setImagesLoaded] = useState<Set<number>>(new Set([0]))

    const slides = content.slides

    // Preload next and previous images
    useEffect(() => {
        const nextIndex = (currentSlide + 1) % slides.length
        const prevIndex = (currentSlide - 1 + slides.length) % slides.length

        const indicesToLoad = [nextIndex, prevIndex].filter(index => !imagesLoaded.has(index))

        indicesToLoad.forEach((index) => {
            const img = new Image()
            img.src = getImageUrl(slides[index].image)
            img.onload = () => {
                setImagesLoaded((prev) => {
                    const newSet = new Set(prev)
                    newSet.add(index)
                    return newSet
                })
            }
        })
    }, [currentSlide, slides.length])

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length)
        }, 5000)

        return () => clearInterval(timer)
    }, [slides.length])

    const scrollToIntro = useCallback(() => {
        const element = document.getElementById('intro')
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' })
        }
    }, [])

    const goToSlide = useCallback((index: number) => {
        setCurrentSlide(index)
    }, [])

    const goToPrevious = useCallback(() => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
    }, [slides.length])

    const goToNext = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, [slides.length])

    return (
        <section id="hero" className="restaurant-hero relative overflow-hidden">
            {/* Background Images Slider */}
            <div className="restaurant-hero-background">
                <AnimatePresence mode="wait" initial={false}>
                    <m.div
                        key={currentSlide}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7 }}
                        className="restaurant-hero-bg-image"
                        style={{
                            backgroundImage: `url(${getImageUrl(slides[currentSlide].image)})`,
                        }}
                    />
                </AnimatePresence>
                <div className="restaurant-hero-overlay" />
            </div>

            {/* Content */}
            <div className="restaurant-hero-content relative z-10">
                <AnimatePresence mode="wait" initial={false}>
                    <m.div
                        key={currentSlide}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                    >
                        {/* Main Title */}
                        <div className="mb-12">
                            <div>
                                <h1 className="restaurant-hero-title">{slides[currentSlide].title}</h1>
                                <p className="restaurant-hero-year">
                                    {slides[currentSlide].subtitle}
                                </p>
                            </div>

                            {/* Decorative Line */}
                            <div className="flex items-center justify-center gap-4 my-8">
                                <div className="h-px w-24 bg-gradient-to-r from-transparent to-white/50" />
                                <div className="w-2 h-2 bg-red-600 rotate-45" />
                                <div className="h-px w-24 bg-gradient-to-l from-transparent to-white/50" />
                            </div>
                        </div>

                        {/* Subtitle */}
                        <div className="mb-12">
                            <p className="restaurant-hero-subtitle">{slides[currentSlide].description}</p>
                            <p className="restaurant-hero-description">Since 1931</p>
                        </div>
                    </m.div>
                </AnimatePresence>

                {/* CTA — 슬라이드와 무관하게 고정 노출 (전환 시 흔들리지 않도록 정적 배치) */}
                <div className="restaurant-hero-cta">
                    <a
                        href={NAVER_BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="restaurant-hero-cta-button restaurant-hero-cta-reserve"
                        aria-label={t('cta.reserveAria')}
                        onClick={() => trackCtaClick('reserve', 'hero')}
                    >
                        <CalendarCheck size={18} />
                        <span>{t('cta.reserveFull')}</span>
                    </a>
                    <a
                        href={PHONE_LINK}
                        className="restaurant-hero-cta-button restaurant-hero-cta-phone"
                        aria-label={t('cta.phoneAria')}
                        onClick={() => trackCtaClick('call', 'hero')}
                    >
                        <PhoneCall size={18} />
                        <span>{t('cta.phoneFull')}</span>
                    </a>
                </div>
            </div>

            {/* Navigation Arrows */}
            <button
                onClick={goToPrevious}
                className="restaurant-hero-nav-button restaurant-hero-nav-prev"
                aria-label="이전 슬라이드"
            >
                <ChevronLeft size={24} />
            </button>
            <button
                onClick={goToNext}
                className="restaurant-hero-nav-button restaurant-hero-nav-next"
                aria-label="다음 슬라이드"
            >
                <ChevronRight size={24} />
            </button>

            {/* Slide Indicators */}
            <div className="restaurant-hero-indicators">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`restaurant-hero-indicator ${index === currentSlide ? 'active' : ''}`}
                        aria-label={`슬라이드 ${index + 1}로 이동`}
                    />
                ))}
            </div>

            {/* Scroll Indicator */}
            <div className="restaurant-scroll-indicator">
                <m.button
                    onClick={scrollToIntro}
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    className="restaurant-scroll-button"
                    aria-label="아래로 스크롤"
                >
                    <ChevronDown size={20} />
                </m.button>
            </div>
        </section>
    )
}

export default React.memo(Hero)
